import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSchoolsOptionsService } from "../../services/plantels/plantel.service";
import {
  closeFormModal,
  openViewModal,
} from "../../store/reducers/plantels/plantels.reducer";
import {
  createPlantelThunk,
  getPlantelsThunk,
  updatePlantelThunk,
  getPlantelByIdThunk,
} from "../../store/reducers/plantels/plantels.thunks";
import Alert from "../UI/Alert";
import Loader from "../UI/Loader";
import Modal from "../UI/Modal";
import Select2 from "../UI/Select2";
import { toast } from "react-toastify";

const PlantelFormModal = () => {
  const dispatch = useDispatch();
  const { formModalOpen, formMode, selectedPlantel, formLoading, formError } =
    useSelector((state) => state.plantels);

  const [formData, setFormData] = useState({
    nombre: "",
    clave: "",
    escuela: null,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (formMode === "edit" && selectedPlantel) {
      setFormData({
        nombre: selectedPlantel.nombre || "",
        clave: selectedPlantel.clave || "",
        escuela: selectedPlantel.escuela || null,
      });
    } else {
      setFormData({
        nombre: "",
        clave: "",
        escuela: null,
      });
    }
    setErrors({});
  }, [formMode, selectedPlantel, formModalOpen]);

  const handleClose = () => {
    dispatch(closeFormModal());
    setFormData({
      nombre: "",
      clave: "",
      escuela: null,
    });
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = "El nombre es requerido";
    }

    if (!formData.clave.trim()) {
      newErrors.clave = "La clave es requerida";
    }

    if (!formData.escuela) {
      newErrors.escuela = "La escuela es requerida";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    const payload = {
      nombre: formData.nombre.trim(),
      clave: formData.clave.trim(),
      escuela: formData.escuela,
    };

    let result;
    let recordId;
    if (formMode === "create") {
      result = await dispatch(createPlantelThunk(payload));
      console.log("CREATE payload:", result.payload);

      if (result.meta.requestStatus === "fulfilled") {
        recordId = result.payload.data.id;
        toast.success("Plantel creado exitosamente");
      }
    } else {
      result = await dispatch(
        updatePlantelThunk({
          id: selectedPlantel.id,
          payload,
        }),
      );
      if (result.meta.requestStatus === "fulfilled") {
        recordId = selectedPlantel.id;
        toast.success("Plantel actualizado exitosamente");
      }
    }

    if (result.meta.requestStatus === "fulfilled") {
      await dispatch(getPlantelsThunk({ page: 1 }));
      handleClose();
      // Abrir modal de vista con el registro
      await dispatch(getPlantelByIdThunk(recordId));
      dispatch(openViewModal(recordId));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSchoolChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      escuela: value,
    }));
    if (errors.escuela) {
      setErrors((prev) => ({
        ...prev,
        escuela: null,
      }));
    }
  };

  const fetchSchoolsOptions = async (search = "") => {
    try {
      const data = await getSchoolsOptionsService(search);
      return data;
    } catch (error) {
      console.error("Error al cargar escuelas:", error);
      return [];
    }
  };

  return (
    <Modal
      open={formModalOpen}
      title={formMode === "create" ? "Crear Plantel" : "Editar Plantel"}
      size="xl"
      onClose={handleClose}
    >
      <form onSubmit={handleSubmit}>
        {formError && (
          <Alert type="danger" message={formError} className="mb-3" />
        )}

        <div className="row g-3">
          <div className="col-md-12">
            <label htmlFor="escuela" className="form-label">
              Escuela <span className="text-danger">*</span>
            </label>
            <Select2
              value={formData.escuela}
              onChange={handleSchoolChange}
              fetchOptions={fetchSchoolsOptions}
              placeholder="Selecciona una escuela..."
              required
              errorMessage={errors.escuela}
            />
            {errors.escuela && (
              <div className="invalid-feedback d-block">{errors.escuela}</div>
            )}
          </div>

          <div className="col-md-6">
            <label htmlFor="nombre" className="form-label">
              Nombre <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <Icon icon="mdi:office-building" width="20" height="20" />
              </span>
              <input
                type="text"
                className={`form-control ${errors.nombre ? "is-invalid" : ""}`}
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Ej: Plantel Centro"
                disabled={formLoading}
              />
              {errors.nombre && (
                <div className="invalid-feedback">{errors.nombre}</div>
              )}
            </div>
          </div>

          <div className="col-md-6">
            <label htmlFor="clave" className="form-label">
              Clave <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <Icon icon="mdi:identifier" width="20" height="20" />
              </span>
              <input
                type="text"
                className={`form-control ${errors.clave ? "is-invalid" : ""}`}
                id="clave"
                name="clave"
                value={formData.clave}
                onChange={handleChange}
                placeholder="Ej: PL-001"
                disabled={formLoading}
              />
              {errors.clave && (
                <div className="invalid-feedback">{errors.clave}</div>
              )}
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={formLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary d-flex align-items-center gap-2"
            disabled={formLoading}
          >
            {formLoading ? (
              <>
                <Loader type="spinner" size="sm" delay={0} />
                Guardando...
              </>
            ) : (
              <>
                <Icon icon="mdi:content-save" width="20" height="20" />
                {formMode === "create" ? "Crear" : "Actualizar"}
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PlantelFormModal;
